import axios from 'axios';
import { API_URL } from './constants';

const abortControllers = {};

export const fetchDataSourcesOverview = async () => {
    try {
        const response = await axios.get(`${API_URL}/data-sources-overview`);
        return response.data;
    } catch (error) {
        console.error('Error fetching data sources overview:', error);
        return null;
    }
};

export const fetchHistogramData = async () => {
    try {
        const response = await axios.get(`${API_URL}/histogram-data`);
        return response.data;
    } catch (error) {
        console.error('Error fetching histogram data:', error);
        return null;
    }
};


export const fetchPositionData = async (retailer, category, year, month, day) => {
    try {
        const response = await axios.get(`${API_URL}/position_data?retailer=${retailer}&category=${category}&year=${year}&month=${month}&day=${day}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching histogram data:', error);
        return null;
    }
};


export const fetchPlacementData = async (retailer, category, year, month, day) => {
    try {
        const response = await axios.get(`${API_URL}/placement_data`, {
            params: {
                retailer: retailer,
                category: category,
                year: year,
                month: month,
                day: day
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching placement data:', error);
        return null;
    }
};

export const getColumnsUniqueValues = async (projectKey, viewType) => {
    const endpoint = `${API_URL}/${projectKey}/${viewType}/columns_unique_values`;

    // Check if there is a previous request for this endpoint and abort it
    if (abortControllers[endpoint]) {
        abortControllers[endpoint].abort('Operation canceled due to new request.');
    }

    // Create a new AbortController instance
    const controller = new AbortController();
    abortControllers[endpoint] = controller;

    try {
        const { data } = await axios.get(endpoint, {
            signal: controller.signal
        });
        return data;
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log(`Request '${endpoint}' canceled:`, error.message);
        } else {
            console.error('Error fetching columns unique values:', error);
        }
        return null;
    }
};

export const getMinAndMaxDate = async (projectKey, viewType) => {
    const endpoint = `${API_URL}/${projectKey}/${viewType}/min_and_max_date`;

    // Check if there is a previous request for this endpoint and abort it
    if (abortControllers[endpoint]) {
        abortControllers[endpoint].abort('Operation canceled due to new request.');
    }

    // Create a new AbortController instance
    const controller = new AbortController();
    abortControllers[endpoint] = controller;

    try {
        const { data } = await axios.get(endpoint, {
            signal: controller.signal
        });
        return data;
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log(`Request '${endpoint}' canceled:`, error.message);
        } else {
            console.error('Error fetching min and max dates:', error);
        }
        return null;
    }
};

export const filterProjectProductViewData = async (
    projectKey,
    viewType,
    countries,
    retailers,
    brands,
    products,
    currency,
    inSaleStatus,
    startDateString,
    endDateString,
    lineType,
    packSizes,
    categories,
    referenceEans = null
) => {
    const endpoint = `${API_URL}/${projectKey}/${viewType}/page_data`;

    // Check if there is a previous request for this endpoint and abort it
    if (abortControllers[endpoint]) {
        abortControllers[endpoint].abort('Operation canceled due to new request.');
    }

    // Create a new AbortController instance
    const controller = new AbortController();
    abortControllers[endpoint] = controller;

    try {
        const normalizedBrands = (() => {
            if (!brands) return null;
            if (Array.isArray(brands)) return brands.length ? brands : null;
            // Backward compatibility: allow single brand string.
            return `${brands}`.trim() ? [`${brands}`] : null;
        })();

        const { data } = await axios.post(endpoint, {
            countries: countries,
            retailers: retailers,
            brands: normalizedBrands,
            products: products,
            reference_eans: referenceEans && referenceEans.length ? referenceEans : null,
            currency: currency,
            in_sale_status: inSaleStatus,
            start_date: startDateString,
            end_date: endDateString,
            line_type: lineType || null,
            pack_sizes: packSizes && packSizes.length ? packSizes : null,
            categories: categories && categories.length ? categories : null,
        }, {
            signal: controller.signal,
            headers: {
                'content-type': 'application/json'
            }
        });
        return data;
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log(`Request '${endpoint}' canceled:`, error.message);
        } else {
            console.error('Error fetching product view data:', error);
        }
        return null;
    }
};

export const fetchRawData = async ({
    projectKey,
    viewType,
    countries,
    retailers,
    brands,
    referenceEans = null,
    currency,
    inSaleStatus,
    startDateString,
    endDateString,
    packSizes,
    categories,
    page = 0,
    pageSize = 100,
}) => {
    const endpoint = `${API_URL}/${projectKey}/${viewType}/raw_data`;

    if (abortControllers[endpoint]) {
        abortControllers[endpoint].abort('Operation canceled due to new request.');
    }
    const controller = new AbortController();
    abortControllers[endpoint] = controller;

    try {
        const { data } = await axios.post(endpoint, {
            countries,
            retailers,
            brands: brands && brands.length ? brands : null,
            reference_eans: referenceEans && referenceEans.length ? referenceEans : null,
            currency,
            in_sale_status: inSaleStatus,
            start_date: startDateString,
            end_date: endDateString,
            pack_sizes: packSizes && packSizes.length ? packSizes : null,
            categories: categories && categories.length ? categories : null,
            page,
            page_size: pageSize,
        }, {
            signal: controller.signal,
            headers: { 'content-type': 'application/json' },
        });
        return data;
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log(`Request '${endpoint}' canceled:`, error.message);
        } else {
            console.error('Error fetching raw data:', error);
        }
        return null;
    }
};

export const refreshBatProductData = async (startDateString, endDateString) => {
    const endpoint = `${API_URL}/bat/product_view/refresh_data`;

    try {
        const { data } = await axios.post(endpoint, {
            start_date: startDateString,
            end_date: endDateString,
        }, {
            headers: { 'content-type': 'application/json' }
        });
        return data;
    } catch (error) {
        console.error('Error refreshing BAT product data:', error);
        return null;
    }
};

export const refreshAsahiProductData = async (startDateString, endDateString) => {
    const endpoint = `${API_URL}/asahi/product_view/refresh_data`;

    try {
        const { data } = await axios.post(endpoint, {
            start_date: startDateString,
            end_date: endDateString,
        }, {
            headers: { 'content-type': 'application/json' }
        });
        return data;
    } catch (error) {
        console.error('Error refreshing Asahi product data:', error);
        return null;
    }
};

export const refreshProjectProductData = async (projectKey, startDateString, endDateString) => {
    if (projectKey === 'asahi') {
        return refreshAsahiProductData(startDateString, endDateString);
    }
    if (projectKey === 'bat' || projectKey === 'pmi' || projectKey === 'jti-sweden') {
        return refreshBatProductData(startDateString, endDateString);
    }
    return null;
};

export const refreshBatDistributionData = async (startDateString, endDateString) => {
    const endpoint = `${API_URL}/bat/distribution_view/refresh_data`;

    try {
        const { data } = await axios.post(endpoint, {
            start_date: startDateString,
            end_date: endDateString,
        }, {
            headers: {
                'content-type': 'application/json'
            }
        });
        return data;
    } catch (error) {
        console.error('Error refreshing distribution data:', error);
        return null;
    }
};

export const refreshProjectDistributionData = async (projectKey, startDateString, endDateString) => {
    if (projectKey === 'bat' || projectKey === 'pmi') {
        return refreshBatDistributionData(startDateString, endDateString);
    }
    return null;
};

export const filterProjectRetailerViewData = async (
    projectKey,
    viewType,
    retailers,
    brands,
    products,
    currency,
    startDateString,
    endDateString
) => {
    const endpoint = `${API_URL}/${projectKey}/${viewType}/page_data`;

    // Check if there is a previous request for this endpoint and abort it
    if (abortControllers[endpoint]) {
        abortControllers[endpoint].abort('Operation canceled due to new request.');
    }

    // Create a new AbortController instance
    const controller = new AbortController();
    abortControllers[endpoint] = controller;

    try {
        const { data } = await axios.post(endpoint, {
            retailers: retailers,
            brands: brands,
            products: products,
            currency: currency,
            start_date: startDateString,
            end_date: endDateString
        }, {
            signal: controller.signal,
            headers: {
                'content-type': 'application/json'
            }
        });
        return data;
    } catch (error) {
        console.error('Error fetching retailer view data:', error);
        return null;
    }
};

export const filterProjectOutOfStockData = async (
    projectKey,
    viewType,
    retailers,
    brands,
    statuses,
    oosWhileInSale,
    startDateString,
    endDateString
) => {
    const endpoint = `${API_URL}/${projectKey}/${viewType}/page_data`;

    // Check if there is a previous request for this endpoint and abort it
    if (abortControllers[endpoint]) {
        abortControllers[endpoint].abort('Operation canceled due to new request.');
    }

    // Create a new AbortController instance
    const controller = new AbortController();
    abortControllers[endpoint] = controller;

    try {
        const { data } = await axios.post(endpoint, {
            retailers: retailers,
            brands: brands,
            statuses: statuses,
            oos_while_in_sale: oosWhileInSale,
            start_date: startDateString,
            end_date: endDateString
        }, {
            signal: controller.signal,
            headers: {
                'content-type': 'application/json'
            }
        });
        return data;
    } catch (error) {
        if (axios.isCancel(error)) {
            console.log(`Request '${endpoint}' aborted:`, error.message);
        } else {
            console.error('Error fetching distribution view (brand scope) data:', error);
        }
        return null;
    }
};

export const fetchRealEstateState = async (limit = 25, offset = 0) => {
    try {
        const response = await axios.get(`${API_URL}/real_estate/sreality/state`, {
            params: {
                limit: limit,
                offset: offset
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching real estate state data:', error);
        return null;
    }
};

export const fetchRealEstateDetailed = async (limit = 25, offset = 0) => {
    try {
        const response = await axios.get(`${API_URL}/real_estate/sreality/detailed-data`, {
            params: {
                limit: limit,
                offset: offset
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching real estate detailed data:', error);
        return null;
    }
};

export const predictPrice = async (features) => {
    try {
        const response = await axios.post(`${API_URL}/real_estate/model/predict-price`, features);
        return response.data;
    } catch (error) {
        console.error('Error predicting price:', error);
        throw error;
    }
};

